import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '@/lib/axios';
import type { User } from '@/features/auth/api/auth.api';

interface AuthState {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<User>;
    register: (name: string, email: string, password: string) => Promise<void>;
    logout: () => void;
    setUser: (user: User | null) => void;
    checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,

            login: async (email: string, password: string) => {
                set({ isLoading: true });
                try {
                    const response = await api.post('/auth/login', { email, password });
                    const { user, token, refreshToken } = response.data.data;
                    localStorage.setItem('token', token);
                    // Store refreshToken if provided (backend uses cookies, but we store as fallback)
                    if (refreshToken) {
                        localStorage.setItem('refreshToken', refreshToken);
                    }
                    set({ user, token, isAuthenticated: true, isLoading: false });
                    return user;
                } catch (error) {
                    set({ isLoading: false });
                    throw error;
                }
            },

            register: async (name: string, email: string, password: string) => {
                set({ isLoading: true });
                try {
                    const response = await api.post('/auth/register', { name, email, password });
                    const { user, token, refreshToken } = response.data.data;
                    localStorage.setItem('token', token);
                    // Store refreshToken if provided (backend uses cookies, but we store as fallback)
                    if (refreshToken) {
                        localStorage.setItem('refreshToken', refreshToken);
                    }
                    set({ user, token, isAuthenticated: true, isLoading: false });
                } catch (error) {
                    set({ isLoading: false });
                    throw error;
                }
            },

            logout: () => {
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
                api.post('/auth/logout').catch(console.error);
                set({ user: null, token: null, isAuthenticated: false });
            },

            setUser: (user: User | null) => {
                set({ user, isAuthenticated: !!user });
            },

            checkAuth: async () => {
                const token = get().token || localStorage.getItem('token');
                if (!token) {
                    set({ user: null, token: null, isAuthenticated: false });
                    return;
                }

                try {
                    // Set token in axios header before making request
                    const response = await api.get('/auth/me');
                    const { user } = response.data.data;
                    set({ user, token, isAuthenticated: true });
                } catch {
                    localStorage.removeItem('token');
                    set({ user: null, token: null, isAuthenticated: false });
                }
            },
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({ token: state.token }),
            onRehydrateStorage: () => (state) => {
                // After rehydrating token from localStorage, check auth
                if (state?.token) {
                    // Call checkAuth after a short delay to ensure axios interceptor is ready
                    setTimeout(() => {
                        state.checkAuth();
                    }, 100);
                }
            },
        }
    )
);

export default useAuthStore;
